import {
  Box,
  MethodOption,
  PhoneMockup,
  PhoneMockupLayout,
  Text,
} from '@frontend/ui-library';

import { getIdFromUrl } from '@frontend/utils';
import { useEffect, useState } from 'react';
import { BackButton, BillDetailHeader } from '../../components';
import { useWidgetContext } from '../../context/WidgetContext';
import { PageFields_CurrenciesPage_Fragment as CurrenciesPageFragment } from '../../graphql/generated';
import { useCurrencySubmission } from '../../hooks/useCurrencySubmission';
import { ApiChainSymbols, ApiCurrencySymbols } from '../../utils/enums';
import { MethodApiId } from '../../data/methods';
import { solanaWallets } from '../../data/solanaWallets';
import { PaymentMethod } from '../../graphql/customTypes';
import { isOfframpBill } from '../../utils';

interface SolanaPayWalletSelectionProps {
  data: CurrenciesPageFragment['pageData'];
  onSelectionError: () => void;
  onGoBack?: () => void;
}

export default function SolanaPayWalletSelection({
  data,
  onSelectionError,
  onGoBack,
}: SolanaPayWalletSelectionProps) {
  const { expiresAt, fiatTotal, fiatCurrency, merchantName, billType } = data;

  const paymentId = getIdFromUrl();
  const [selectedWalletId, setSelectedWalletId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { configureHeader } = useWidgetContext();
  const { loading, submitCurrencySelection } = useCurrencySubmission({
    onSelectionError,
  });

  useEffect(() => {
    configureHeader({
      allowClosePayment: true,
      expiresAt: expiresAt,
      id: paymentId,
      merchantName,
      billType,
      onMobileBackButtonClick: () => onGoBack,
    });
  }, [expiresAt, paymentId, merchantName, billType, configureHeader, onGoBack]);

  const handleWalletSelection = async (walletId: string) => {
    try {
      setSelectedWalletId(walletId);
      setIsSubmitting(true);

      await submitCurrencySelection({
        currency: ApiCurrencySymbols.USDC,
        network: ApiChainSymbols.SOL,
        methodId: MethodApiId.SolanaPay,
        paymentMethod: PaymentMethod.WALLET,
      });
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const isWalletLoading = (walletId: string) => {
    return selectedWalletId === walletId && (loading || isSubmitting);
  };

  const isOffRamp = isOfframpBill(billType);

  return (
    <PhoneMockupLayout backButton={<BackButton onClick={onGoBack} />}>
      <PhoneMockup
        header={
          <BillDetailHeader
            amount={fiatTotal}
            currency={fiatCurrency}
            isOffRamp={isOffRamp}
          />
        }
      >
        <Text
          mb="0"
          mt="0"
          as="h1"
          size="big"
          weight="700"
          color="royal-700"
          align="center"
        >
          Select your Solana Pay wallet
        </Text>
        <Text
          mt="8"
          mb="16"
          as="p"
          weight="400"
          size="small"
          color="neutral-500"
          align="center"
        >
          This method requires wallets that support it. If you don’t see yours,
          please select a different network.
        </Text>

        {solanaWallets.map((wallet) => (
          <Box key={wallet.id} mt="8">
            <MethodOption
              methodName={wallet.name}
              methodIconSrc={wallet.iconSrc}
              onClick={() => handleWalletSelection(wallet.id)}
              disabled={loading || isSubmitting}
              loading={isWalletLoading(wallet.id)}
            />
          </Box>
        ))}
      </PhoneMockup>
    </PhoneMockupLayout>
  );
}
